// 「用其他身分看」的 React 這一層（規則在 viewAs.ts）：
//
//   App 路由把導演頁、工作站包在 <ViewAsProvider> 裡；頁面和裡面的元件用：
//     const user = useViewAsUser();            // 預覽時 = 導演 / 那個動畫師（角色、id、名字都換掉）；平常 = 登入的人
//     const va = useViewAs();
//     va.projectRole(myRole)                   // 預覽時換成預覽的角色
//     <button {...va.ro('原本的說明')} onClick={...}>   // 寫入的按鈕：預覽時照樣能按（試按），送出去的寫入在瀏覽器被擋
//     onSomething={va.guard(fn)}               // 會寫資料的動作：預覽時照樣執行；送出的那一步被擋下來，提示一句「不會存」
//
//   預覽開著時（只有製作或這個專案的製作、網址有 ?as=）：
//     - 先讀專案成員（要知道看的是哪個動畫師），讀完才畫頁面 —— 也保證「擋寫入」已經裝好了才開始讀資料
//     - 擋下所有寫入的請求（installWriteGuard，導演、動畫師都一樣；不會離開瀏覽器）；離開預覽就拿掉
//     - 畫面上可以像那個角色一樣試按每個功能；被擋下來的寫入統一跳中性提示（toast.viewAsBlocked，連點只說一次）
//     - 上面一條橫幅（components/ViewAsBanner，App 路由用 components/ViewAsPage 包起來）：在看誰的畫面、換成導演 / 動畫師、回到編輯

import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as api from './api';
import type { AuthUser } from './api';
import type { ProjectMember, ProjectRole } from './mockData';
import { useAuth } from './auth';
import { installWriteGuard, parseViewAs, viewAsIdentity, type ViewAs } from './viewAs';
import { RouteLoading } from './roleRouting';

/** 寫入按鈕要加的屬性（預覽也能按，所以只剩原本的說明） */
export interface ReadOnlyProps {
  title?: string;
}

export interface ViewAsState {
  /** 正在預覽（製作 + 網址有 ?as=） */
  active: boolean;
  viewAs: ViewAs | null;
  /** 預覽也能試按，所以一直是 false（舊的元件還在讀這個欄位） */
  readOnly: false;
  projectId: string | null;
  /** 預覽時畫面上的「我」；不是預覽 = null（用 useViewAsUser 拿） */
  user: AuthUser | null;
  /** 看的是哪個動畫師（找得到這個專案的動畫師才有） */
  person: ProjectMember | null;
  /** 這個專案的動畫師（橫幅上換人用） */
  animators: ProjectMember[];
  /** 預覽時換成預覽的角色 */
  projectRole: (real: ProjectRole | null) => ProjectRole | null;
  /** 寫入按鈕的屬性：就是原本的 title（預覽也能按） */
  ro: (title?: string) => ReadOnlyProps;
  /** 會寫資料的動作：直接執行（預覽時送出的寫入在瀏覽器被擋） */
  guard: <A extends unknown[]>(fn: (...args: A) => void) => (...args: A) => void;
}

const passThrough = <A extends unknown[]>(fn: (...args: A) => void) => fn;

const INACTIVE: ViewAsState = {
  active: false,
  viewAs: null,
  readOnly: false,
  projectId: null,
  user: null,
  person: null,
  animators: [],
  projectRole: (real) => real,
  ro: (title) => (title ? { title } : {}),
  guard: passThrough,
};

const ViewAsCtx = createContext<ViewAsState>(INACTIVE);

export function useViewAs(): ViewAsState {
  return useContext(ViewAsCtx);
}

/** 頁面上的「我」：預覽時是導演 / 那個動畫師；平常就是登入的人 */
export function useViewAsUser(): AuthUser | null {
  const { user } = useAuth();
  const va = useContext(ViewAsCtx);
  return va.active ? va.user : user;
}

// 擋寫入的裝置離開預覽後多留 1 秒再拿掉：離開頁面時各元件的清理動作（例如把沒存完的樂手器材送出去）
// 可能比這裡的清理晚一步才發出請求，不能讓它們在預覽結束的瞬間漏出去。期間又進預覽就沿用同一個。
const GUARD_LINGER_MS = 1000;
let guardHolders = 0;
let guardRemove: (() => void) | null = null;
let guardTimer: ReturnType<typeof setTimeout> | null = null;
function acquireWriteGuard() {
  guardHolders++;
  if (guardTimer) { clearTimeout(guardTimer); guardTimer = null; }
  if (!guardRemove) {
    guardRemove = installWriteGuard(globalThis, (method, url) => {
      console.warn(`[預覽模式] 擋下了一個寫入的請求（${method} ${url}）`);
    });
  }
}
function releaseWriteGuard() {
  guardHolders = Math.max(0, guardHolders - 1);
  if (guardHolders > 0 || guardTimer) return;
  guardTimer = setTimeout(() => {
    guardTimer = null;
    if (guardHolders === 0 && guardRemove) { guardRemove(); guardRemove = null; }
  }, GUARD_LINGER_MS);
}

/** banner = 預覽時最上面那一條（components/ViewAsBanner；從 ViewAsPage 傳進來，這裡不 import 畫面元件） */
export function ViewAsProvider({ children, banner }: { children: ReactNode; banner?: ReactNode }) {
  const { user } = useAuth();
  const { projectId = null } = useParams();
  const [params] = useSearchParams();
  const asParam = params.get('as');
  const userParam = params.get('user');
  const accountRole = user?.role;
  // 帳號不是全站製作、網址又有 ?as= → 先問一次自己在這個專案是不是製作（專案的製作也能用）；問完才決定要不要預覽
  const asWanted = asParam === 'director' || asParam === 'animator';
  const needLookup = !!user && accountRole !== 'admin' && asWanted && !!projectId;
  const lookupKey = user && projectId ? `${user.id}/${projectId}` : '';
  const [looked, setLooked] = useState<{ key: string; role: ProjectRole | null } | null>(null);
  useEffect(() => {
    if (!needLookup || !projectId) return;
    let cancelled = false;
    api.getProjectInfo(projectId)
      .then(info => { if (!cancelled) setLooked({ key: lookupKey, role: info.myRole }); })
      .catch(() => { if (!cancelled) setLooked({ key: lookupKey, role: null }); });
    return () => { cancelled = true; };
  }, [needLookup, projectId, lookupKey]);
  const lookupPending = needLookup && (!looked || looked.key !== lookupKey);
  const projectRoleForView = needLookup && looked && looked.key === lookupKey ? looked.role : null;
  const viewAs = useMemo(() => {
    const q = new URLSearchParams();
    if (asParam) q.set('as', asParam);
    if (userParam) q.set('user', userParam);
    return parseViewAs(q, accountRole, projectRoleForView);
  }, [asParam, userParam, accountRole, projectRoleForView]);
  const active = !!viewAs && !!projectId;

  // 專案成員（看的是哪個動畫師、橫幅上換人）
  const [members, setMembers] = useState<{ projectId: string; list: ProjectMember[] } | null>(null);
  useEffect(() => {
    if (!active || !projectId) return;
    let cancelled = false;
    api.getProjectMeta(projectId)
      .then(p => { if (!cancelled) setMembers({ projectId, list: p?.members ?? [] }); })
      // 讀不到（封存的專案不在清單裡 / 網路）→ 當作沒有成員，頁面自己會說讀不到
      .catch(() => { if (!cancelled) setMembers({ projectId, list: [] }); });
    return () => { cancelled = true; };
  }, [active, projectId]);

  // 擋下寫入的請求（萬一有漏掉的按鈕也改不到資料）
  const [guardOn, setGuardOn] = useState(false);
  useEffect(() => {
    if (!active) return;
    acquireWriteGuard();
    setGuardOn(true);
    return () => { releaseWriteGuard(); setGuardOn(false); };
  }, [active]);

  const memberList = members && members.projectId === projectId ? members.list : null;
  const animators = useMemo(() => (memberList ?? []).filter(m => m.role === 'animator'), [memberList]);
  const person = useMemo(
    () => (viewAs?.previewRole === 'animator' && viewAs.previewUserId ? animators.find(m => m.id === viewAs.previewUserId) ?? null : null),
    [viewAs, animators],
  );
  const effectiveUser = useMemo(
    () => (viewAs && user ? viewAsIdentity(user, viewAs, person) : null),
    [user, viewAs, person],
  );

  const projectRole = useCallback((real: ProjectRole | null) => (viewAs ? viewAs.previewRole : real), [viewAs]);

  const value = useMemo<ViewAsState>(() => (active && viewAs ? {
    active: true,
    viewAs,
    readOnly: false,
    projectId,
    user: effectiveUser,
    person,
    animators,
    projectRole,
    ro: INACTIVE.ro,
    guard: passThrough,
  } : INACTIVE), [active, viewAs, projectId, effectiveUser, person, animators, projectRole]);

  // 橫幅蓋在頁面上面 → 頁面往下讓
  useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    root.classList.add('viewas-on');
    return () => { root.classList.remove('viewas-on'); root.style.removeProperty('--viewas-h'); };
  }, [active]);

  // 還在問「我在這個專案是不是製作」→ 先不畫頁面（免得預覽的頁面在擋寫入裝好之前就開始讀、寫）
  if (lookupPending) return <RouteLoading text="正在打開預覽…" />;
  if (!active) return <>{children}</>;
  const ready = guardOn && memberList !== null;
  return (
    <ViewAsCtx.Provider value={value}>
      {banner}
      {/* 換人 / 換角色 → 整頁重來（不會帶著上一個人選的歌、篩選） */}
      {ready ? <Fragment key={`${viewAs!.previewRole}:${person?.id ?? ''}`}>{children}</Fragment> : <div style={{ paddingTop: 'var(--viewas-h, 36px)' }}><RouteLoading text="正在打開預覽…" /></div>}
    </ViewAsCtx.Provider>
  );
}
